import { permissionsFor } from "@/lib/permissions";
import { useEffect, useState, useMemo } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCustomization } from "@/context/CustomizationContext";
import { fmtCurrency, fmtCurrencyMasked, fmtDate, fmtNum } from "@/lib/format";
import { getEffectiveWorkspaceColumns } from "@/lib/workspaceColumns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import ImportDialog from "@/components/common/ImportDialog";
import ExportButton from "@/components/common/ExportButton";
import PresetsMenu from "@/components/common/PresetsMenu";
import MultiSelectFilter from "@/components/common/MultiSelectFilter";
import { Plus, Pencil, Trash2, Search, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";

const RIGHT_TYPES = new Set(["number", "currency", "percentage"]);

const EMPTY_FORM = {
  process: "", service: "", service_code: "", rev_code: "", pricing: "", percentage_rate: "", custom: {},
  billing_mode: "fte", billing_basis_field: "", percentage_field: "", collection_field: "",
};

const PRICING_MODE_OPTIONS = [
  {
    value: "fte",
    label: "FTE - $",
    unit: "$",
    description: "Rate × client-selected FTE categories, such as Prorated or Billable FTE.",
  },
  {
    value: "transactions",
    label: "Transactions - $",
    unit: "$",
    description: "Rate × transaction criteria defined by the client's invoice structure.",
  },
  {
    value: "man_hours",
    label: "Man Hours - $",
    unit: "$",
    description: "Rate × captured Hours used for productivity tracking and invoicing.",
  },
  {
    value: "percentage",
    label: "Percentage - %",
    unit: "%",
    description: "Percentage × financial or client-defined “Basis” values.",
  },
];

const percentageRateFor = (service) => {
  if (service?.percentage_rate != null && service.percentage_rate !== "") {
    return service.percentage_rate;
  }
  if (service?.percentage_field) {
    return service.custom?.[service.percentage_field];
  }
  return service?.billing_mode === "percentage" ? service?.pricing : null;
};

// ── Table view with sticky header ─────────────────────────────────────────

const ServicesTable = ({ services, allColumns, canEdit, canDelete, canViewFinancial, fmtCustom, onEdit, onDelete, labelOf, selected, onToggleRow, onToggleAll, sortBy, sortDir, onSort }) => {
  const visibleCols = allColumns.filter((c) => c.visible !== false);

  return (
    <div className="rounded-xl border overflow-hidden shadow-[var(--app-shadow-sm)]">
      <div className="overflow-auto max-h-[calc(100vh-220px)]">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="bg-card border-b">
              {canDelete && (
                <th className="sticky top-0 z-10 bg-card px-2 py-2.5 border-b shadow-[0_1px_0_0_hsl(var(--border))] w-8">
                  <Checkbox
                    checked={
                      services.length > 0 && services.every((s) => selected?.has(s.id))
                        ? true
                        : services.some((s) => selected?.has(s.id))
                        ? "indeterminate"
                        : false
                    }
                    onCheckedChange={onToggleAll}
                    aria-label="Select all"
                    data-testid="services-select-all"
                  />
                </th>
              )}
              {visibleCols.map((col) => (
                <th
                  key={col.key}
                  className={`sticky top-0 z-10 bg-card px-3 py-2.5 font-semibold text-foreground whitespace-nowrap border-b shadow-[0_1px_0_0_hsl(var(--border))] ${RIGHT_TYPES.has(col.type) ? "text-right" : "text-left"}`}
                >
                  {(() => {
                    const sortField = col.core ? col.key : `custom.${col.key}`;
                    return (
                      <button
                        type="button"
                        data-testid={`sort-${sortField}`}
                        onClick={() => onSort(sortField)}
                        title={`Sort by ${col.label}`}
                        className={`inline-flex items-center gap-1 rounded px-1 py-1 hover:bg-muted transition-colors ${RIGHT_TYPES.has(col.type) ? "flex-row-reverse" : ""} ${sortBy === sortField ? "text-[hsl(var(--primary))]" : ""}`}
                      >
                        {labelOf(col.key)}
                        {sortBy === sortField
                          ? (sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)
                          : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                      </button>
                    );
                  })()}
                </th>
              ))}
              {canEdit && <th className="sticky top-0 z-10 bg-card px-3 py-2.5 border-b shadow-[0_1px_0_0_hsl(var(--border))]" />}
            </tr>
          </thead>
          <tbody>
            {services.map((s, idx) => (
              <tr
                key={s.id}
                className={`border-b last:border-0 hover:bg-muted/40 transition-colors ${idx % 2 === 1 ? "bg-muted/20" : ""}`}
                data-testid={`service-row-${s.service_code}`}
              >
                {canDelete && (
                  <td className="px-2 py-2">
                    <Checkbox
                      checked={!!selected?.has(s.id)}
                      onCheckedChange={() => onToggleRow(s.id)}
                      aria-label="Select row"
                      data-testid={`service-select-${s.service_code}`}
                    />
                  </td>
                )}
                {visibleCols.map((col) => {
                  let value;
                  if (col.core) {
                    if (col.key === "pricing") {
                      const hasPercentage = s.billing_mode === "percentage";
                      value = col.financial && !canViewFinancial
                        ? <span className="text-slate-400">{hasPercentage ? "%" : "$"}</span>
                        : hasPercentage
                          ? `${fmtNum(percentageRateFor(s), 2)}%`
                          : fmtCurrencyMasked(s.pricing, 2);
                    } else {
                      value = s[col.key] ?? "-";
                    }
                  } else {
                    value = col.financial && !canViewFinancial
                      ? <span className="text-slate-400">{col.type === "percentage" ? "%" : "$"}</span>
                      : fmtCustom(col, s.custom?.[col.key]);
                  }
                  return (
                    <td key={col.key} className={`px-3 py-2 text-foreground ${RIGHT_TYPES.has(col.type) ? "text-right tabular-nums" : ""}`}>
                      {col.key === "service_code" || col.key === "rev_code"
                        ? <Badge variant="outline" className="rounded-full text-[10px] font-mono px-2">{s[col.key] || "-"}</Badge>
                        : value}
                    </td>
                  );
                })}
                {canEdit && (
                  <td className="px-2 py-1.5 text-right">
                    <div className="flex gap-0.5 justify-end">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEdit(s)}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      {canDelete && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-600" onClick={() => onDelete(s)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
            {services.length === 0 && (
              <tr>
                <td colSpan={visibleCols.length + (canEdit ? 1 : 0) + (canDelete ? 1 : 0)} className="text-center py-10 text-muted-foreground text-sm">
                  No services yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────

export default function ServicesPage() {
  const { user } = useAuth();
  const canEdit = permissionsFor(user?.role).canManage;
  const canDelete = permissionsFor(user?.role).canManage;
  const canViewFinancial = permissionsFor(user?.role).canViewFinancial;
  const { getColumns } = useCustomization();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [deleting, setDeleting] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [columnFilters, setColumnFilters] = useState({});
  const [sortBy, setSortBy] = useState("process");
  const [sortDir, setSortDir] = useState("asc");

  const configuredColumns = getColumns("services");
  const allColumns = useMemo(
    () => getEffectiveWorkspaceColumns("services", configuredColumns),
    [configuredColumns],
  );
  const colByKey = useMemo(() => Object.fromEntries(allColumns.map((c) => [c.key, c])), [allColumns]);
  const labelOf = (key) => colByKey[key]?.label || key;
  const customCols = useMemo(() => allColumns.filter((c) => !c.core && c.visible !== false), [allColumns]);
  const allCustomCols = useMemo(() => allColumns.filter((c) => !c.core), [allColumns]);
  const scopeOfWorkColumn = customCols.find((c) => c.key === "custom_scope_of_work");
  const effectiveDateColumn = customCols.find((c) => c.key === "custom_effective_date");
  const otherCustomCols = useMemo(
    () => customCols.filter((c) => !["custom_scope_of_work", "custom_effective_date"].includes(c.key)),
    [customCols],
  );
  const filterColumns = useMemo(() => allColumns.filter((c) => c.visible !== false), [allColumns]);


  const load = () => {
    api.get("/services")
      .then((res) => setServices(res.data))
      .catch(() => toast.error("Failed to load services"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const valueForColumn = (service, column) =>
    column.core ? service[column.key] : service.custom?.[column.key];

  const filterOptions = useMemo(() => {
    const options = {};
    filterColumns.forEach((column) => {
      const values = new Map();
      services.forEach((service) => {
        const raw = valueForColumn(service, column);
        const value = raw === null || raw === undefined || raw === "" ? "__blank__" : String(raw);
        if (!values.has(value)) values.set(value, value === "__blank__" ? "(blank)" : String(raw));
      });
      options[column.key] = [...values.entries()]
        .sort((a, b) => a[1].localeCompare(b[1], undefined, { numeric: true, sensitivity: "base" }))
        .map(([value, label]) => ({ value, label }));
    });
    return options;
  }, [filterColumns, services]);

  const filteredServices = useMemo(() => {
    const query = searchInput.trim().toLowerCase();
    const sortColumn = sortBy.startsWith("custom.")
      ? colByKey[sortBy.slice("custom.".length)]
      : colByKey[sortBy];
    const matches = (service) => {
      const matchesSearch = !query || filterColumns.some((column) => {
        const raw = valueForColumn(service, column);
        return raw !== null && raw !== undefined && String(raw).toLowerCase().includes(query);
      });
      const matchesFilters = filterColumns.every((column) => {
        const selectedValues = columnFilters[column.key] || [];
        if (!selectedValues.length) return true;
        const raw = valueForColumn(service, column);
        const value = raw === null || raw === undefined || raw === "" ? "__blank__" : String(raw);
        return selectedValues.includes(value);
      });
      return matchesSearch && matchesFilters;
    };
    const compare = (left, right) => {
      const leftValue = sortColumn ? valueForColumn(left, sortColumn) : left[sortBy];
      const rightValue = sortColumn ? valueForColumn(right, sortColumn) : right[sortBy];
      const leftEmpty = leftValue === null || leftValue === undefined || leftValue === "";
      const rightEmpty = rightValue === null || rightValue === undefined || rightValue === "";
      if (leftEmpty || rightEmpty) {
        if (leftEmpty && rightEmpty) return 0;
        return leftEmpty ? 1 : -1;
      }
      if (sortColumn && RIGHT_TYPES.has(sortColumn.type)) {
        return (Number(leftValue) || 0) - (Number(rightValue) || 0);
      }
      return String(leftValue).localeCompare(String(rightValue), undefined, {
        numeric: true, sensitivity: "base",
      });
    };
    return services
      .filter(matches)
      .sort((left, right) => sortDir === "asc" ? compare(left, right) : -compare(left, right));
  }, [services, filterColumns, columnFilters, searchInput, sortBy, sortDir, colByKey]);

  const toggleSort = (field) => {
    if (sortBy === field) setSortDir((current) => current === "asc" ? "desc" : "asc");
    else {
      setSortBy(field);
      setSortDir("asc");
    }
    setSelected(new Set());
  };

  const setColumnFilter = (key, values) => {
    setColumnFilters((current) => ({ ...current, [key]: values }));
    setSelected(new Set());
  };

  const currentFilters = {
    search: searchInput,
    column_filters: columnFilters,
    sort_by: sortBy,
    sort_dir: sortDir,
  };

  const applyPreset = (filters) => {
    setSearchInput(filters.search || "");
    setColumnFilters(filters.column_filters || {});
    setSortBy(filters.sort_by || "process");
    setSortDir(filters.sort_dir || "asc");
    setSelected(new Set());
  };

  const fmtCustom = (col, v) => {
    if (col.type === "currency") return v == null ? "$" : fmtCurrency(v, col.decimals ?? 2);
    if (col.type === "percentage") return v == null ? "%" : `${fmtNum(v, col.decimals ?? 2)}%`;
    if (v == null || v === "") return "-";
    if (col.type === "number") return fmtNum(v, col.decimals ?? 2);
    if (col.type === "date") return fmtDate(v);
    if (col.type === "boolean") return v ? "Yes" : "No";
    return String(v);
  };

  const setCustomField = (key, value) => setForm((f) => ({ ...f, custom: { ...f.custom, [key]: value } }));

  const openAdd = () => { setEditing(null); setForm({ ...EMPTY_FORM, custom: {} }); setDialogOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      process: s.process, service: s.service, service_code: s.service_code,
      rev_code: s.rev_code, pricing: s.pricing ?? "", percentage_rate: percentageRateFor(s) ?? "",
      custom: { ...(s.custom || {}) },
      billing_mode: s.billing_mode || "fte",
      billing_basis_field: s.billing_basis_field || s.collection_field || "",
      percentage_field: s.percentage_field || "",
      collection_field: s.collection_field || "",
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.process || !form.service || !form.service_code) {
      toast.error("Process, service, and service code are required.");
      return;
    }
    if (form.billing_mode === "percentage" && form.percentage_rate === "") {
      toast.error("Percentage is required for the Percentage pricing model.");
      return;
    }
    if (form.billing_mode !== "percentage" && form.pricing === "") {
      toast.error("Pricing is required for this pricing model.");
      return;
    }
    setSaving(true);
    const custom = {};
    allCustomCols.forEach((c) => {
      let v = form.custom?.[c.key];
      if (v === "" || v == null) { custom[c.key] = null; return; }
      if (RIGHT_TYPES.has(c.type)) v = parseFloat(v) || 0;
      else if (c.type === "boolean") v = v === true || v === "true";
      custom[c.key] = v;
    });
    const payload = {
      ...form,
      pricing: parseFloat(form.pricing) || 0,
      percentage_rate: form.billing_mode === "percentage"
        ? parseFloat(form.percentage_rate)
        : null,
      custom,
    };
    try {
      if (editing) {
        const res = await api.put(`/services/${editing.id}`, payload);
        toast.success("Service updated; historical records were retained");
      } else {
        await api.post("/services", payload);
        toast.success("Service added");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const renderCustomField = (c) => (
    <div key={c.key} className="space-y-1.5">
      <Label>{c.label}</Label>
      {c.financial && !canViewFinancial ? (
        <div className="h-9 flex items-center rounded-md border bg-slate-50 px-3 text-sm text-slate-400">
          {c.type === "percentage" ? "%" : "$"}
        </div>
      ) : c.type === "dropdown" ? (
        <Select value={String(form.custom?.[c.key] ?? "")}
          onValueChange={(v) => setCustomField(c.key, v)}>
          <SelectTrigger data-testid={`service-form-${c.key}`}><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent>
            {(c.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
      ) : c.type === "boolean" ? (
        <Select value={form.custom?.[c.key] == null ? "" : String(form.custom[c.key])}
          onValueChange={(v) => setCustomField(c.key, v === "true")}>
          <SelectTrigger data-testid={`service-form-${c.key}`}><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="true">Yes</SelectItem>
            <SelectItem value="false">No</SelectItem>
          </SelectContent>
        </Select>
      ) : (
        <Input
          data-testid={`service-form-${c.key}`}
          type={RIGHT_TYPES.has(c.type) ? "number" : c.type === "date" ? "date" : "text"}
          step={RIGHT_TYPES.has(c.type) ? "0.01" : undefined}
          className={RIGHT_TYPES.has(c.type) ? "text-right tabular-nums" : ""}
          value={form.custom?.[c.key] ?? ""}
          onChange={(e) => setCustomField(c.key, e.target.value)}
        />
      )}
    </div>
  );

  const confirmDelete = async () => {
    try {
      await api.delete(`/services/${deleting.id}`);
      toast.success("Service deleted");
      setDeleting(null);
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const toggleRow = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => {
    const allSelected = filteredServices.length > 0 && filteredServices.every((s) => selected.has(s.id));
    setSelected(allSelected ? new Set() : new Set(filteredServices.map((s) => s.id)));
  };

  const confirmBulkDelete = async () => {
    const ids = [...selected];
    try {
      await Promise.all(ids.map((id) => api.delete(`/services/${id}`)));
      toast.success(`${ids.length} service${ids.length !== 1 ? "s" : ""} deleted`);
      setSelected(new Set());
      setBulkDeleting(false);
      load();
    } catch {
      toast.error("Some deletions failed — please retry");
    }
  };

  if (loading) return <Skeleton className="h-[500px] rounded-xl" data-testid="services-loading" />;

  return (
    <div className="space-y-5" data-testid="services-page">
      {/* ── Toolbar ──────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm text-slate-600 shrink-0">
          Service catalog (Legend) · <span className="font-medium">{filteredServices.length} of {services.length} services</span>
        </p>
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            data-testid="services-search-input"
            placeholder="Search all columns..."
            className="pl-9 rounded-lg"
            value={searchInput}
            onChange={(event) => { setSearchInput(event.target.value); setSelected(new Set()); }}
          />
        </div>
        {filterColumns.map((column) => (
          <MultiSelectFilter
            key={column.key}
            label={column.label}
            testId={`services-${column.key}-filter`}
            className="max-w-[180px]"
            options={filterOptions[column.key] || []}
            selected={columnFilters[column.key] || []}
            onChange={(values) => setColumnFilter(column.key, values)}
          />
        ))}
        {canDelete && selected.size > 0 && (
          <Button variant="destructive" size="sm" className="rounded-lg"
            data-testid="services-bulk-delete-button"
            onClick={() => setBulkDeleting(true)}>
            <Trash2 className="h-4 w-4 mr-1" /> Delete {selected.size} selected
          </Button>
        )}
        <div className="ml-auto flex items-center gap-2">
          <PresetsMenu pageKey="services" currentFilters={currentFilters} onApply={applyPreset} />
          {canViewFinancial && (
            <ImportDialog entity="services" entityLabel="Services"
              testIdPrefix="services" onImported={load} />
          )}
          {canViewFinancial && <ExportButton testId="services-export-button" />}
          {canEdit && (
            <Button data-testid="services-add-button" onClick={openAdd} size="sm" className="rounded-lg">
              <Plus className="h-4 w-4 mr-1" /> Add Service
            </Button>
          )}
        </div>
      </div>

      <ServicesTable
        services={filteredServices}
        allColumns={allColumns}
        canEdit={canEdit}
        canDelete={canDelete}
        canViewFinancial={canViewFinancial}
        fmtCustom={fmtCustom}
        onEdit={openEdit}
        onDelete={setDeleting}
        labelOf={labelOf}
        selected={selected}
        onToggleRow={toggleRow}
        onToggleAll={toggleAll}
        sortBy={sortBy}
        sortDir={sortDir}
        onSort={toggleSort}
      />

      {/* ── Add/Edit dialog ───────────────────────────────────────────────── */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-[540px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Service" : "Add Service"}</DialogTitle>
            <DialogDescription>
              {editing
                ? "Catalog changes apply to new selections. Existing revenue and engagement records retain their stored values."
                : "Add a new service to the catalog."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>{labelOf("service")} *</Label>
              <Input data-testid="service-form-service" placeholder="e.g. AR Follow-up"
                value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>{labelOf("process")} *</Label>
              <Input data-testid="service-form-process" placeholder="e.g. Revenue Cycle Management (RCM)"
                value={form.process} onChange={(e) => setForm({ ...form, process: e.target.value })} />
            </div>
            {scopeOfWorkColumn && renderCustomField(scopeOfWorkColumn)}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{labelOf("service_code")} *</Label>
                <Input data-testid="service-form-code" placeholder="e.g. ARF"
                  value={form.service_code} onChange={(e) => setForm({ ...form, service_code: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>{labelOf("rev_code")}</Label>
                <Input data-testid="service-form-revcode" placeholder="e.g. AHARFI"
                  value={form.rev_code} onChange={(e) => setForm({ ...form, rev_code: e.target.value })} />
              </div>
            </div>
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">Pricing Model</legend>
              <div className="grid grid-cols-2 gap-2" data-testid="service-pricing-model">
                {PRICING_MODE_OPTIONS.map((option) => (
                  <label
                    key={option.value}
                    className={`flex cursor-pointer items-start gap-2 rounded-lg border p-3 transition-colors ${
                      form.billing_mode === option.value
                        ? "border-[hsl(var(--primary))] bg-teal-50/70 ring-1 ring-[hsl(var(--primary))] dark:bg-teal-950/30"
                        : "hover:bg-muted/50"
                    }`}
                  >
                    <input
                      type="radio"
                      name="service-pricing-model"
                      value={option.value}
                      checked={form.billing_mode === option.value}
                      onChange={(e) => setForm({ ...form, billing_mode: e.target.value })}
                      data-testid={`service-pricing-model-${option.value}`}
                      className="mt-0.5 accent-[hsl(var(--primary))]"
                    />
                    <span>
                      <span className="block text-sm font-medium">{option.label}</span>
                      <span className="block text-xs text-muted-foreground">{option.description}</span>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
            <div className="space-y-1.5">
              <Label>{labelOf("pricing")} ($) {form.billing_mode !== "percentage" ? "*" : ""}</Label>
              {canViewFinancial ? (
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  data-testid="service-form-pricing"
                  className="text-right tabular-nums"
                  disabled={form.billing_mode === "percentage"}
                  value={form.pricing}
                  onChange={(e) => setForm({ ...form, pricing: e.target.value })}
                />
              ) : (
                <div className="h-9 flex items-center rounded-md border bg-slate-50 px-3 text-sm text-slate-400" data-testid="service-form-pricing-masked">
                   $
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Percentage (%) {form.billing_mode === "percentage" ? "*" : ""}</Label>
              {canViewFinancial ? (
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  inputMode="decimal"
                  data-testid="service-form-percentage"
                  className="text-right tabular-nums"
                  disabled={form.billing_mode !== "percentage"}
                  value={form.percentage_rate}
                  onChange={(e) => setForm({ ...form, percentage_rate: e.target.value })}
                />
              ) : (
                <div className="h-9 flex items-center rounded-md border bg-slate-50 px-3 text-sm text-slate-400" data-testid="service-form-percentage-masked">
                   %
                </div>
              )}
            </div>
            {effectiveDateColumn && renderCustomField(effectiveDateColumn)}

            {otherCustomCols.length > 0 && (
              <>
                <div className="border-t pt-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Custom Fields</p>
                </div>
                {otherCustomCols.map(renderCustomField)}
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button data-testid="service-form-save-button" onClick={save} disabled={saving}>
              {saving ? "Saving..." : editing ? "Save Changes" : "Add Service"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Bulk delete confirm ───────────────────────────────────────────── */}
      <AlertDialog open={bulkDeleting} onOpenChange={(o) => !o && setBulkDeleting(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selected.size} service{selected.size !== 1 ? "s" : ""}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete {selected.size} selected service{selected.size !== 1 ? "s" : ""}. Existing revenue lines keep their recorded pricing. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="services-bulk-delete-confirm-button"
              onClick={confirmBulkDelete} className="bg-red-600 hover:bg-red-700">
              Delete {selected.size} service{selected.size !== 1 ? "s" : ""}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Delete confirm ────────────────────────────────────────────────── */}
      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete service?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleting && `${deleting.service} (${deleting.service_code}) · ${fmtCurrencyMasked(deleting.pricing, 0)}`}
              <br />Existing revenue lines keep their recorded pricing. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="service-delete-confirm-button" onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
